import type { Ref } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import { TripPlannerHeaderIconButton } from 'tripPlanner/components/Header/Shared'
import TripPlannerHomeLogoLink from 'tripPlanner/components/Header/TripPlannerHomeLogoLink'
import { useItineraryContext } from './ChatTripPlannerPage/contexts/ItineraryContext'
import { useTripPlannerNavigation } from './ChatTripPlannerPage/hooks/useTripPlannerNavigation'
import TripPlannerNavigation from './TripPlannerNavigation'
import { TRIP_PLANNER_HEADER_HEIGHT, TRIP_PLANNER_NAVIGATION_GUTTER, TRIP_PLANNER_SIDE_NAV_ID } from './tripPlannerNavigationConstants'

const SIDE_NAV_WIDTH = rem(256)
const SIDE_NAV_LARGE_WIDTH = rem(272)
// Matches the prototype's collapse timing, short enough to read as a response to the send.
const SIDE_NAV_TRANSITION_MS = 220

const SideNavElement = styled.nav`
  display: none;
  flex-shrink: 0;
  inline-size: ${SIDE_NAV_WIDTH};
  overflow: hidden;
  border-inline-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.collapsed {
    inline-size: 0;
    border-inline-end-width: 0;
  }

  &.animated {
    transition: inline-size ${SIDE_NAV_TRANSITION_MS}ms ease;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  ${mediaQueryUp.desktop} {
    display: flex;
  }

  ${mediaQueryUp.largeDesktop} {
    inline-size: ${SIDE_NAV_LARGE_WIDTH};
  }

  @media print {
    display: none;
  }
`

const SideNavContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  min-inline-size: ${SIDE_NAV_WIDTH};
  min-block-size: 0;
  padding-block-end: ${rem(12)};

  ${mediaQueryUp.largeDesktop} {
    min-inline-size: ${SIDE_NAV_LARGE_WIDTH};
  }
`

const SideNavHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  block-size: ${rem(TRIP_PLANNER_HEADER_HEIGHT)};
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
  padding-inline: ${rem(TRIP_PLANNER_NAVIGATION_GUTTER)} ${rem(8)};
`

interface Props {
  isExpanded: boolean
  isAnimated: boolean
  onCollapse: () => void
  collapseButtonRef: Ref<HTMLButtonElement>
  isLandingView: boolean
  activeChatId: number | undefined
  onStartNewChat: () => void
  onChatSelect: (chatId: number) => void
  onChatDeleted: (deletedChatId: number, fallbackChatId: number | undefined) => void
  isDevMode: boolean
  isRewindMode: boolean
  onDevModeToggle: () => void
  onRewindModeToggle: () => void
}

function TripPlannerSideNav({
  isExpanded,
  isAnimated,
  onCollapse,
  collapseButtonRef,
  isLandingView,
  activeChatId,
  onStartNewChat,
  onChatSelect,
  onChatDeleted,
  isDevMode,
  isRewindMode,
  onDevModeToggle,
  onRewindModeToggle,
}: Props) {
  const intl = useIntl()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isStaff = useAppSelector(isLEStaff)
  const { hasItineraryData } = useItineraryContext()
  const navigation = useTripPlannerNavigation({
    isLoggedIn,
    onChatDeleted,
    activeChatId,
    hasActiveChatItinerary: hasItineraryData,
  })

  // Remounts the nav body on a landing↔chat change, so a search typed on the landing doesn't outlive it.
  const navigationKey = isLandingView ? 'landing' : 'chat'

  const navigationLabel = intl.formatMessage({
    id: 'NC0lFoVG2kaplEHk-AKI',
    defaultMessage: 'Trip planner navigation',
    description: 'Accessible name for the persistent TripPlanner side nav that lists chats on desktop',
  })
  const collapseLabel = intl.formatMessage({
    id: '9vJ1d-cmXr1au7oQe1VC',
    defaultMessage: 'Collapse navigation',
    description: 'Accessible label for the button that collapses the persistent TripPlanner side nav on desktop',
  })

  return (
    <SideNavElement
      id={TRIP_PLANNER_SIDE_NAV_ID}
      aria-label={navigationLabel}
      className={clsx({ collapsed: !isExpanded, animated: isAnimated })}
      inert={!isExpanded}
    >
      <SideNavContent>
        <SideNavHeader>
          <TripPlannerHomeLogoLink />
          <TripPlannerHeaderIconButton
            ref={collapseButtonRef}
            onClick={onCollapse}
            aria-label={collapseLabel}
            aria-expanded={isExpanded}
            aria-controls={TRIP_PLANNER_SIDE_NAV_ID}
          >
            <LineTimesIcon />
          </TripPlannerHeaderIconButton>
        </SideNavHeader>
        <TripPlannerNavigation
          key={navigationKey}
          {...navigation}
          isLoggedIn={isLoggedIn}
          isLandingView={isLandingView}
          onStartNewChat={onStartNewChat}
          onChatSelect={onChatSelect}
          activeChatId={activeChatId}
          isStaff={isStaff}
          isDevMode={isDevMode}
          onDevModeToggle={onDevModeToggle}
          isRewindMode={isRewindMode}
          onRewindModeToggle={onRewindModeToggle}
        />
      </SideNavContent>
    </SideNavElement>
  )
}

export default TripPlannerSideNav
